import { useEffect, useState } from "react";
import { toast } from "sonner";
import { StripePlan } from "@/types";
import { secureStorage } from "@/utils/secureStorage";
import { StripeRepository } from "@/infrastructure/repositories/StripeRepository";

interface ProSubscriptionProductProps {
  onClose?: () => void;
}

type UserType = "swimmer" | "coach";
type PlanKey = "monthly" | "yearly";

const PRODUCT_CONFIG = {
  swimmer: {
    title: "Upgrade to Swimming's Smartest AI",
    subtitle:
      "Empower your swimming journey with access to the world's best coaches, AI support, and a vibrant community.",
    features: [
      {
        title: "Swim Practice Scanner",
        description: "Track your sets and practices by simply taking a picture!",
      },
      {
        title: "Product Marketplace Access",
        description:
          "Hire a professional swim coach. Purchase sets and technique analysis from the world's best coaches.",
      },
      {
        title: "AI Practice Set Generator",
        description: "Create personalized practice sets using Swimnerd AI.",
      },
      {
        title: "Community Chat",
        description: "Connect and chat with other swimmers and coaches.",
      },
    ],
  },
  coach: {
    title: "Upgrade to Coach smarter. Earn more.",
    subtitle:
      "Unlock powerful tools to manage your swimming business and support your swimmers with AI-driven features.",
    features: [
      {
        title: "Product Marketplace",
        description: "List and sell your swimming-related products.",
      },
      {
        title: "Custom AI Agent",
        description:
          "Build your own AI agent using your expertise to assist you and your swimmers.",
      },
      {
        title: "Swim Practice Scanner",
        description:
          "Track your sets and practices by simply taking a picture or screenshot!",
      },
      {
        title: "Ask Swimnerd AI Anything",
        description:
          "Create personalized practices. Troubleshoot Meet Manager. Ask about a drill.",
      },
    ],
  },
};

const formatAmount = (amount: number) => {
  if (!Number.isFinite(amount)) return "0.00";
  const normalized = Math.round((amount + Number.EPSILON) * 100) / 100;
  return normalized.toFixed(2);
};

const chooseProductPlans = (plans: StripePlan[], userType: UserType) => {
  const filteredPlans = plans.filter((plan) => {
    const name = String(plan.product_name || "").toLowerCase();
    const nickname = String(plan.nickname || "").toLowerCase();
    return !name.includes("test") && !nickname.includes("test");
  });

  const grouped = new Map<string, StripePlan[]>();

  for (const plan of filteredPlans) {
    const key = plan.product_id || "__unknown__";
    const current = grouped.get(key) ?? [];
    current.push(plan);
    grouped.set(key, current);
  }

  const groups = Array.from(grouped.values());
  if (groups.length === 0) return [];

  const coachGroups = groups.filter((group) =>
    group.some((plan) => String(plan.product_name || "").toLowerCase().includes("coach"))
  );

  if (userType === "coach" && coachGroups.length > 0) return coachGroups[0];

  if (userType === "swimmer") {
    const nonCoachGroups = groups.filter(
      (group) =>
        !group.some((plan) => String(plan.product_name || "").toLowerCase().includes("coach"))
    );
    if (nonCoachGroups.length > 0) return nonCoachGroups[0];
  }

  const sorted = groups.sort((a, b) => {
    const aMax = Math.max(...a.map((x) => Number(x.amount) || 0));
    const bMax = Math.max(...b.map((x) => Number(x.amount) || 0));
    return userType === "coach" ? bMax - aMax : aMax - bMax;
  });

  return sorted[0] ?? [];
};

export function ProSubscriptionProduct({ onClose }: ProSubscriptionProductProps) {
  const [loadingPlan, setLoadingPlan] = useState<PlanKey | null>(null);
  const [plansLoading, setPlansLoading] = useState(true);
  const [monthlyPlan, setMonthlyPlan] = useState<StripePlan | null>(null);
  const [yearlyPlan, setYearlyPlan] = useState<StripePlan | null>(null);

  const stripeRepository = new StripeRepository();
  const userType = secureStorage.get<UserType>("user_type") ?? "swimmer";
  const userId = localStorage.getItem("current_user_swimmer_id") ?? "";
  const productConfig = PRODUCT_CONFIG[userType];

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        setLoadingPlan(null);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    const loadPlans = async () => {
      try {
        setPlansLoading(true);
        const response = await stripeRepository.getPlans();
        const data = Array.isArray(response?.data) ? response.data : [];
        const selectedPlans = chooseProductPlans(data, userType);

        const monthly =
          selectedPlans.find((plan) => String(plan.interval).toLowerCase() === "month") ?? null;
        const yearly =
          selectedPlans.find((plan) => String(plan.interval).toLowerCase() === "year") ?? null;

        setMonthlyPlan(monthly);
        setYearlyPlan(yearly);
      } catch (error) {
        console.error("Failed to load stripe plans:", error);
        setMonthlyPlan(null);
        setYearlyPlan(null);
      } finally {
        setPlansLoading(false);
      }
    };

    loadPlans();
  }, [userType]);

  const handleSubscribe = async (planKey: PlanKey) => {
    const selectedPlan = planKey === "monthly" ? monthlyPlan : yearlyPlan;
    if (!selectedPlan?.price_id) {
      toast.error("Selected plan is unavailable right now.");
      return;
    }

    setLoadingPlan(planKey);

    if (!userId) {
      toast.error("User session not found. Please log in again.");
      setLoadingPlan(null);
      return;
    }

    try {
      const response = await stripeRepository.createCheckoutSession({
        user_id: String(userId),
        price_id: selectedPlan.price_id,
        mode: "subscription",
        quantity: "1",
        success_url: `${window.location.origin}/app?payment=complete&session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${window.location.origin}/app?payment=complete&canceled=true&session_id={CHECKOUT_SESSION_ID}`,
      });

      const payload = response ?? {};
      const data = payload?.data ?? payload;
      const checkoutUrl = data?.url || data?.checkout_url;
      const sessionId = data?.id || data?.session_id;

      if (!checkoutUrl || !sessionId) {
        throw new Error("Missing checkout url or session id");
      }

      secureStorage.set("stripe_checkout_session_id", sessionId);
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error("Failed to create checkout session:", error);
      toast.error("Unable to start checkout. Please try again.");
      setLoadingPlan(null);
    }
  };

  const showBestValue =
    !!monthlyPlan &&
    !!yearlyPlan &&
    Number(yearlyPlan.amount) < Number(monthlyPlan.amount) * 12;

  return (
    <div className="relative max-w-4xl mx-auto p-6">
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Close paywall"
          className="absolute top-4 right-4 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/95 text-gray-900 shadow-sm ring-1 ring-black/10 transition hover:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800/95 dark:text-gray-100 dark:ring-white/10 dark:hover:bg-gray-700"
        >
          <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
            <path
              d="M6 6l12 12M18 6l-12 12"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      )}

      <h2 className="text-4xl font-bold text-center mb-4 dark:text-white">{productConfig.title}</h2>

      <p className="text-gray-600 dark:text-gray-300 text-center mb-8 text-lg">
        {productConfig.subtitle}
      </p>

      <ul className="space-y-4 mb-12">
        {productConfig.features.map((feature, index) => (
          <li key={index} className="flex gap-3">
            <span className="text-blue-600 text-xl">✓</span>
            <div>
              <p className="font-semibold dark:text-white">{feature.title}</p>
              <p className="text-gray-600 dark:text-gray-400 text-sm">{feature.description}</p>
            </div>
          </li>
        ))}
      </ul>

      <p className="text-center text-sm text-gray-500 mb-6">
        Start with a <span className="font-semibold">7-day free trial</span>. No charge today.
        Cancel anytime.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="border rounded-2xl p-6 shadow bg-white dark:bg-gray-900">
          <h3 className="text-xl font-semibold dark:text-white mb-2">Monthly</h3>
          <p className="text-4xl font-bold text-blue-600 mb-1">
            {monthlyPlan ? `$${formatAmount(Number(monthlyPlan.amount))}` : "--"}
          </p>
          <p className="text-gray-500 mb-6">{monthlyPlan ? "per month" : "Not available"}</p>
          <button
            disabled={loadingPlan !== null || plansLoading || !monthlyPlan}
            onClick={() => handleSubscribe("monthly")}
            className={`w-full py-3 rounded-lg text-white font-semibold ${
              loadingPlan === "monthly" || plansLoading || !monthlyPlan
                ? "bg-gray-400"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {plansLoading ? "Loading..." : loadingPlan === "monthly" ? "Redirecting..." : "Start Free Trial"}
          </button>
        </div>

        <div className="border rounded-2xl p-6 shadow bg-white dark:bg-gray-900 relative">
          {showBestValue && (
            <span className="absolute top-4 right-4 bg-green-600 text-white text-xs px-3 py-1 rounded-full">
              Best Value
            </span>
          )}

          <h3 className="text-xl font-semibold dark:text-white mb-2">Yearly</h3>
          <p className="text-4xl font-bold text-green-600 mb-1">
            {yearlyPlan ? `$${formatAmount(Number(yearlyPlan.amount))}` : "--"}
          </p>
          <p className="text-gray-500 mb-6">{yearlyPlan ? "per year" : "Not available"}</p>
          <button
            disabled={loadingPlan !== null || plansLoading || !yearlyPlan}
            onClick={() => handleSubscribe("yearly")}
            className={`w-full py-3 rounded-lg text-white font-semibold ${
              loadingPlan === "yearly" || plansLoading || !yearlyPlan
                ? "bg-gray-400"
                : "bg-green-600 hover:bg-green-700"
            }`}
          >
            {plansLoading ? "Loading..." : loadingPlan === "yearly" ? "Redirecting..." : "Start Free Trial"}
          </button>
        </div>
      </div>
    </div>
  );
}
